import type RpcResponse    from "@/core/services/Rpc/RpcResponse";
import TransportOrder from "@/domains/TransportOrders/TransportOrder";
import BaseDAO             from "@/core/services/BaseDAO";
import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";
import type Partner from "@/domains/Partners/Partner";
import type Store from "@/domains/Stores/Store";
import type TransportZone from "@/domains/TransportZones/TransportZone";

/**
 * DAO для ЗТК (заявок на транспортные консолидации)
 *
 * Сигнатуры актуализированы по TransportOrdersController.php
 */
class TransportOrdersDAO extends BaseDAO {

    static async get(id: number): Promise<RpcResponse> {
        return await this.rpc.makeRequest('transportOrders.get', { id });
    }

    static async getList(
      filters?: {
        idPattern?: number;
        isOfferBased?: boolean;
        isSamples?: boolean;
        orderCategory?: { id: string };
        deliveryType?: { id: string };
        sender?: { id: number };
        senderCompany?: { id: number };
        receiver?: { id: number };
        receiverCompany?: { id: number };
        responsible?: { id: number };
        // синтетическое фронтовое поле, выкручиваюсь из-за схемы работы дата грида
        isInWork?: boolean,
        isConfirmed?: boolean;
        isFinished?: boolean;
        pickupDatePlannedPeriod?: { from: string; to: string };
        pickupDateActualPeriod?: { from: string; to: string };
        arrivalDatePlannedPeriod?: { from: string; to: string };
        arrivalDateActualPeriod?: { from: string; to: string };
      },
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    ): Promise<RpcResponse> {
        const localParams = {};
        // Тк у датагрида нет возможности инвертировать поля, выкручиваюсь так
        if (filters?.isInWork) {
          localParams.isConfirmed = false;
          localParams.isFinished = false;
          delete filters.isInWork
        }

        // Дата грид накладывает некоторые ограничения, приходится вот так изворачиваться
        if (filters?.isConfirmed === true && filters?.isFinished === undefined) {
          localParams.isFinished = false;
        }

        return await this.rpc.makeRequest(
          'transportOrders.getList',
          {
            ...filters,
            ...localParams,
            orderCategory: filters?.orderCategory ? { id: filters.orderCategory.id } : undefined,
            deliveryType: filters?.deliveryType ? { id: filters.deliveryType.id } : undefined,
            sender: filters?.sender ? { id: filters.sender.id } : undefined,
            senderCompany: filters?.senderCompany ? { id: filters.senderCompany.id } : undefined,
            receiver: filters?.receiver ? { id: filters.receiver.id } : undefined,
            receiverCompany: filters?.receiverCompany ? { id: filters.receiverCompany.id } : undefined,
            responsible: filters?.responsible ? { id: filters.responsible.id } : undefined,
          },
          pagination,
          sort
        );
    }

    static async add(
        transportOrder: TransportOrder,
        receivers?: any[],
        SWPortalTask?: any,
    ): Promise<RpcResponse> {
        return await this.rpc.makeRequest('transportOrders.add', {
            transportOrder,
            receivers,
            SWPortalTask,
        });
    }

    static async update(
        transportOrder: TransportOrder,
        updateReasonText?: string,
    ): Promise<RpcResponse> {
        return await this.rpc.makeRequest('transportOrders.update', {
            transportOrder,
            updateReasonText,
        });
    }

    static async delete(id: number, updateReasonText?: string): Promise<RpcResponse> {
        return await this.rpc.makeRequest('transportOrders.delete', {
            transportOrder: { id },
            updateReasonText,
        });
    }

    static async getXls(id: number): Promise<RpcResponse> {
        return await this.rpc.makeRequest('transportOrders.getXls', {
            transportOrder: { id },
        });
    }

    static async sendNotification(id: number, receivers: any[]): Promise<RpcResponse> {
        return await this.rpc.makeRequest('transportOrders.sendNotification', {
            transportOrder: { id },
            receivers,
        });
    }

    static async getTransportableDocumentsPaginated(
        filters?: {
          transportOrder?: TransportOrder;
          documentClass?: string[];
          sender?: Partner;
          buyerStore?: Store;
          sellerTransportZone?: TransportZone[];
          excludeIds?: number[];
        },
        pagination?: RpcDataPagination,
        sort?: RpcDataSort,
    ): Promise<RpcResponse> {
        return await this.rpc.makeRequest(
            'transportOrders.getTransportableDocuments',
            {
              ...filters,
              transportOrder: filters?.transportOrder ? { id: filters.transportOrder.id } : undefined,
              sender: filters?.sender ? { id: filters.sender.id } : undefined,
              buyerStore: filters?.buyerStore ? { id: filters.buyerStore.id } : undefined,
              sellerTransportZone: filters?.sellerTransportZone ? filters.sellerTransportZone.map((zone) => { return { id: zone.id } }) : undefined,
            },
            pagination,
            sort,
            true, null, null, false,
            { abortPrevious: true },
        );
    }

}

export default TransportOrdersDAO;
